<template>
    <div class="relative mb-4 flex flex-col gap-2">
        <vx-box class="relative flex p-4 gap-2 items-start flex-wrap lg:flex-nowrap lg:py-3 lg:pl-6 lg:pr-3">
            <div class="w-[100px] shrink-0 order-1 pt-2">
                <font-awesome-icon widthAuto :icon="faShareNodes" class="mr-2 text-sky-500"></font-awesome-icon>
                <span class="whitespace-nowrap text-slate-800 text-base font-extrabold">
                    {{ props.matchIndex === 0 ? 'If' : 'And If' }}
                </span>
            </div>
            <div class="grow w-full flex flex-col gap-2 order-2 lg:order-1 lg:grow-0">
                <div
                    data-test="attribute-name-wrapper"
                    :class="customAttributeKeyAndValue() ? 'col-span-12' : 'col-span-7'"
                >
                    <vx-selectable
                        placeholder="attribute name"
                        :model-value="match.attribute_name"
                        :options="attributes"
                        @update:model-value="match.attribute_name = $event"
                        :reduce="(option) => option.value"
                        :clearable="false"
                        :error="displayFirstError(validate.attribute_name.$errors)"
                    >
                        <template v-slot:no-options> Select a Trigger Event to see options. </template>
                        <template v-slot:selected-option="{ option }">
                            <div
                                v-if="option.value === 'message.sentiment' || option.value === 'message.wants_opt_out'"
                                class="flex items-center gap-2"
                            >
                                <span>{{ option.label.toLowerCase() }}</span>
                                <experimental-pill></experimental-pill>
                            </div>
                            <template v-else>
                                {{ option.label.toLowerCase() }}
                            </template>
                        </template>
                        <template v-slot:option="{ option }">
                            <div
                                v-if="option.value === 'message.sentiment' || option.value === 'message.wants_opt_out'"
                                class="flex items-center gap-2"
                            >
                                <span>{{ option.label.toLowerCase() }}</span>
                                <experimental-pill></experimental-pill>
                            </div>
                            <template v-else>
                                {{ option.label.toLowerCase() }}
                            </template>
                        </template>
                    </vx-selectable>
                </div>

                <div data-test="operator-wrapper" class="col-span-5" v-show="showOperator">
                    <vx-selectable
                        placeholder="operator"
                        v-model="match.operator"
                        :getOptionLabel="(option) => option.label.toLowerCase()"
                        :reduce="(option) => option.value"
                        :options="operators"
                        :clearable="false"
                        :error="displayFirstError(validate.operator.$errors)"
                    >
                    </vx-selectable>
                </div>

                <div
                    data-test="value-wrapper"
                    :class="{
                        'col-span-12': showOperator,
                        'col-span-5': !showOperator,
                    }"
                    v-show="showValue"
                >
                    <vx-selectable
                        v-if="valueOptions().length"
                        placeholder="value"
                        v-model="match.value"
                        :reduce="(option) => option.value"
                        :options="valueOptions()"
                        :clearable="false"
                        :error="displayFirstError(validate.value.$errors)"
                    />
                    <div v-else-if="apiCall" class="flex gap-2">
                        <vx-selectable
                            placeholder="value"
                            :model-value="specialValue"
                            @update:model-value="specialValue = $event"
                            :options="state.autocompleteItems"
                            :loading="state.autocompleteLoading"
                            :reduce="(option) => option?.value || option"
                            :create-option="(option) => option"
                            @search="fetchOptions"
                            :multiple="multiple"
                            :taggable="isTaggable"
                            :clearable="false"
                            :error="displayFirstError(validate.value.$errors)"
                        />
                        <resource-details-popover
                            v-if="campaignIdAttribute() && match.value"
                            class="lg:-mr-14"
                            :teamId="teamId"
                            :hover="false"
                            :placement="is2Xl ? 'top' : 'left'"
                            :modelValue="{ id: match.value, resource_type: 'campaign' }"
                        >
                            <vx-button type="button" color="muted-light">
                                <font-awesome-icon widthAuto :icon="faCircleInfo"></font-awesome-icon>
                            </vx-button>
                        </resource-details-popover>
                    </div>
                    <vx-selectable
                        v-else-if="isTaggable"
                        placeholder="value"
                        v-model="specialValue"
                        multiple
                        :taggable="true"
                        :clearable="false"
                        :error="displayFirstError(validate.value.$errors)"
                    />
                    <vx-input
                        v-else
                        v-model="match.value"
                        type="text"
                        placeholder="value"
                        :error="displayFirstError(validate.value.$errors)"
                    ></vx-input>
                </div>
            </div>
            <div class="flex ml-auto order-1 lg:order-2">
                <vx-button
                    @click.prevent="removeMatch(props.matchGroupIndex, props.matchIndex)"
                    type="button"
                    size="md"
                    color="muted-light"
                    data-test="remove-filter"
                >
                    <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                </vx-button>
            </div>
        </vx-box>

        <vx-box class="relative p-4 ml-10 lg:ml-[122px] lg:p-6" v-if="currentTimeAttribute()">
            <line-nested></line-nested>

            <div class="font-extrabold text-slate-800 mb-4">Day Range</div>

            <div class="flex flex-col gap-8">
                <time-range-selector
                    v-for="(range, rangeIndex) in state.time_ranges"
                    :key="range.id"
                    v-model="state.time_ranges[rangeIndex]"
                    @add="addTimeRange(rangeIndex)"
                    @remove="removeTimeRange(rangeIndex)"
                />
            </div>

            <div class="font-extrabold text-slate-800 mb-4 mt-6">Time Zone</div>
            <vx-selectable
                data-test="time-zone-select"
                placeholder="Time Zone"
                v-model="state.selectedTimeZone"
                :options="timeZones"
                :clearable="false"
            >
            </vx-selectable>
        </vx-box>

        <vx-box
            data-test="custom-attribute-key-and-value-wrapper"
            class="relative p-4 ml-10 lg:ml-[122px] lg:p-6"
            v-if="customAttributeKeyAndValue()"
        >
            <line-nested></line-nested>

            <div class="font-extrabold text-slate-800 mb-4">Custom Attribute</div>
            <div class="flex flex-col gap-2 lg:gap-4 lg:grid lg:grid-cols-12">
                <div class="col-span-6">
                    <vx-selectable
                        placeholder="key"
                        data-test="custom-attribute-key"
                        v-model="match.key"
                        :options="state.autocompleteItems"
                        :loading="state.autocompleteLoading"
                        :reduce="(option) => option?.value || option"
                        :create-option="(option) => option"
                        @search="fetchOptions"
                        :taggable="true"
                        :clearable="false"
                        :error="displayFirstError(validate.key.$errors)"
                    />
                </div>

                <div class="col-span-3">
                    <vx-selectable
                        placeholder="data type"
                        data-test="custom-attribute-data-type"
                        v-model="match.data_type"
                        :options="baseDataTypes"
                        :getOptionLabel="(option) => option.label.toLowerCase()"
                        :reduce="(option) => option.value"
                        :clearable="false"
                        :error="displayFirstError(validate.data_type.$errors)"
                    >
                    </vx-selectable>
                </div>

                <div class="col-span-3">
                    <vx-selectable
                        placeholder="operator"
                        data-test="custom-attribute-operator"
                        v-model="match.operator"
                        :getOptionLabel="(option) => option.label.toLowerCase()"
                        :reduce="(option) => option.value"
                        :options="operators"
                        :clearable="false"
                        :error="displayFirstError(validate.operator.$errors)"
                    >
                    </vx-selectable>
                </div>

                <div v-if="!nullableValue" class="col-span-12">
                    <vx-input
                        placeholder="value"
                        data-test="custom-attribute-value"
                        v-model="match.value"
                        type="text"
                        :error="displayFirstError(validate.value.$errors)"
                    ></vx-input>
                </div>
            </div>
        </vx-box>
    </div>
</template>

<script setup>
import TimeRangeSelector from '~/components/general/TimeRangeSelector.vue';
import { phoneAreaCodes } from '~/components/automation/phone-area-codes';
import { entryPointsForSelect } from '~/components/filters';
import { subscriptionStatusForSelect } from '~/constants/contactSubscription';
import timeZones from '~/utils/timeZones';
import axios from '~/services/axios';
import { computed, h, inject, nextTick, onMounted, reactive, watch } from 'vue';
import { useAutomationRule } from './useAutomationRule';
import { useAutomationRulesMetadata } from './useAutomationRulesMetadata';
import {
    MATCH_OPERATORS,
    MATCH_EQUALS_OPERATOR,
    MATCH_EQUALS_ANY_OPERATOR,
    MATCH_IS_NULL_OPERATOR,
    MATCH_IS_NOT_NULL_OPERATOR,
} from './matchItemHelpers';
import { randomString } from '~/utils/string';
import { useToasted } from '~/composables/useToasted';
import { VxInput, VxSelectable, VxBox, VxButton } from '@voxie/frontend-components';
import { getClientTz } from '~/utils/date';
import { faCircleInfo, faShareNodes, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';
import debounce from '~/utils/debounce';
import ExperimentalPill from '../general/ExperimentalPill.vue';
import ResourceDetailsPopover from '~/components/general/resources/ResourceDetailsPopover.vue';
import { useMediaQuery } from '@vueuse/core';

const LineNested = h('div', {
    class: 'absolute top-9 -left-[25px] w-6 h-px border-solid border-y border-x-0 border-t-slate-300 border-b-white lg:-left-20 lg:w-[78px]',
});

const FEATURES = inject('FEATURES');

const toasted = useToasted();

const is2Xl = useMediaQuery('(min-width: 1536px)');

const props = defineProps({
    matchGroupIndex: {
        type: Number,
        required: true,
    },
    matchIndex: {
        type: Number,
        required: true,
    },
});

const { triggers } = useAutomationRulesMetadata();
const { data, removeMatch } = useAutomationRule();

const baseDataTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Numeric' },
];

const state = reactive({
    autocompleteLoading: false,
    autocompleteItems: [],

    selectedTimeZone: getClientTz(),
    time_ranges: [],
});

const match = defineModel();
const validate = useVuelidate(
    {
        attribute_name: {
            required: helpers.withMessage('Attribute is required', required),
        },
        key: {
            requiredIf: helpers.withMessage(
                'Key is required',
                requiredIf(() => customAttributeKeyAndValue())
            ),
        },
        data_type: {
            required: helpers.withMessage(
                'Data Type is required',
                requiredIf(() => customAttributeKeyAndValue())
            ),
        },
        operator: {
            required: helpers.withMessage('Operator is required', required),
        },
        value: {
            requiredIf: helpers.withMessage(
                'Value is required',
                requiredIf(() => !nullableValue.value)
            ),
            maxLength: maxLength(191),
        },
    },
    match
);

const multiple = computed(() => !campaignIdAttribute() && !automationIdAttribute());

// used for apiCall, isTaggable
const specialValue = computed({
    set: (newValue) => {
        if (!Array.isArray(newValue)) {
            // for campaign id and automation id handling
            // its not an array since its not multiple
            // we still want to parse the value
            newValue = [newValue];
        }

        // for taggable it returns only the label
        const commaValues = (newValue || [])
            .map((option) => option?.value || option?.label || option)
            .filter((option) => Boolean(option))
            .join(',');
        match.value.value = commaValues;
    },
    get: () => {
        return (match.value.value || '').split(',').filter((value) => value?.length);
    },
});

const usingAttribute = (attributeName) => {
    return (data.value.match_groups || []).some((matchGroup) => {
        return (matchGroup.matches || []).some((match) => match.attribute_name === attributeName);
    });
};

const attributes = computed(() => {
    const raw = triggers.value.find((trigger) => trigger.value === data.value.event)?.attributes;
    if (!raw) {
        return [];
    }

    return Object.keys(raw).reduce((all, key) => {
        // hide sentiment and wants opt out when the sentiment analysis feature is not enabled.
        // only hides if not using it already if the feature was enabled, then disabled the match
        // will be still be present here.
        if (
            ((key === 'message.sentiment' && !usingAttribute('message.sentiment')) ||
                (key === 'message.wants_opt_out' && !usingAttribute('message.wants_opt_out'))) &&
            !FEATURES.sentiment_analysis_ai
        ) {
            return all;
        }
        all.push({
            value: key,
            label: raw[key],
        });
        return all;
    }, []);
});

const teamId = Spark.state.currentTeam.id;

const apiCall = computed(() => {
    if (match.value.attribute_name === 'contact.tags.tag_name') {
        return {
            url: `/api/v3/teams/${teamId}/tags/autocomplete`,
            showValue: false,
        };
    }

    if (customAttributeKeyAndValue() || customAttributeKey()) {
        return {
            url: `/api/v3/teams/${teamId}/custom-attributes/autocomplete`,
            showValue: true,
        };
    }

    if (campaignIdAttribute()) {
        return {
            url: `/api/v3/teams/${teamId}/campaigns/autocomplete`,
            showValue: true,
        };
    }

    if (automationIdAttribute()) {
        return {
            url: `/api/v3/teams/${teamId}/search/automation-rules`,
            showValue: true,
        };
    }

    return null;
});

const entryPointAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'contact.entry_point';
};

const subscriptionStatusAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return (
        attr === 'contact.marketing_subscription' ||
        attr === 'contact.transactional_subscription' ||
        attr === 'contact.previous_marketing_subscription' ||
        attr === 'contact.previous_transactional_subscription'
    );
};

const originTypeAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'message.origin_type';
};

const messageDirectionAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'message.direction';
};

const messageBodySentimentAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'message.sentiment';
};

const messageBodyWantsOptOutAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'message.wants_opt_out';
};

const phoneStateAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'contact.phone_state';
};

const customAttributeKeyAndValue = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'contact.custom_attributes.key_value' || attr === 'custom_attribute.key_value';
};

const customAttributeKey = (attributeName) => {
    if (customAttributeKeyAndValue(attributeName)) {
        return false;
    }

    const attr = attributeName || match.value.attribute_name;

    return attr === 'contact.custom_attributes.key' || attr === 'custom_attribute.key';
};

const mediaUrlAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'message.media_url';
};

const currentTimeAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'current_time';
};

const campaignIdAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return (
        attr === 'campaign.id' ||
        attr === 'contact.campaign_ids' ||
        attr === 'message.antecedent_campaign_id' ||
        attr === 'short_url_click.message_campaign_id'
    );
};
const automationIdAttribute = (attributeName) => {
    const attr = attributeName || match.value.attribute_name;

    return attr === 'message.antecedent_automation_rule_id' || attr === 'short_url_click.message_automation_rule_id';
};

const isTaggable = computed(() => {
    return [
        'custom_attribute.key',
        'contact.custom_attributes.key',
        'custom_attribute.key_value',
        'contact.custom_attributes.key_value',
        'contact.tags.tag_name',
        'contact.custom_attributes.value',
    ].includes(match.value.attribute_name);
});

const nullableValue = computed(() => {
    return ['is_null', 'is_not_null', 'is_true', 'is_false'].includes(match.value.operator);
});

onMounted(async () => {
    if (currentTimeAttribute()) {
        // transforms 'America/New_York; 1_08:00-1_17:00; 2_08:00-2_17:00; 3_08:00-3_17:00; 4_08:00-4_21:33; 5_08:00-5_17:00; 6_13:00-6_17:00;'
        // into a workable array of objects and sets the timezone: state.selectedTimeZone = 'America/New_York';
        // array of objects example:
        // [ { day: 0, from: '08:00', to: '17:00' }, { day: 1, from: '08:00', to: '17:00' }, ... ]

        const rawValue = match.value.value || '';
        state.selectedTimeZone = rawValue.split(';')[0] || getClientTz(); // grabs tz

        let ranges = rawValue
            .replace(state.selectedTimeZone, '')
            .split(';')
            .filter((piece) => !!piece) // removes empty values
            .map((piece) => {
                const [from, to] = piece.split('-');
                return {
                    id: randomString(),
                    day: Number(from.split('_')[0].trim()),
                    from: from.split('_')[1].trim(),
                    to: to.split('_')[1].trim(),
                };
            });

        if (!ranges.length) {
            ranges = [makeTimeRange()];
        }

        state.time_ranges = ranges;
    }
    await nextTick();
    // necessary to prefill the campaign name and automation name
    if (campaignIdAttribute() || automationIdAttribute()) {
        fetchOptions(match.value.value);
    }
});

watch(
    () => data.value.event,
    async () => {
        await nextTick();
        // if a newly selected event doesn't provide the same attributes for existing matches
        // then deselect the attribute name / operator / value; effectively resetting the match row
        // TODO: Retain when it's the same but for different initial resource e.g.
        // TODO: contact.phone is shared across multiple event types
        if (match.value.attribute_name) {
            const exists = Boolean(attributes.value.find((option) => option.value == match.value.attribute_name));
            if (!exists) {
                match.value.attribute_name = null;
                match.value.operator = null;
                match.value.value = null;
                match.value.key = null;
                match.value.data_type = null;
            }
        }
    },
    {
        immediate: true,
    }
);

watch(
    () => match.value.value,
    () => {
        if (!match.value.value) {
            return;
        }

        // set custom operator for predefined value and default operator when value option provides it
        const predefinedOperator = valueOptions().find((option) => option.value === match.value.value)?.operator_value;
        if (predefinedOperator) {
            match.value.operator = predefinedOperator;
        }
    }
);

watch(
    [() => state.time_ranges, () => state.selectedTimeZone],
    () => {
        if (!currentTimeAttribute()) {
            return;
        }

        //Sunday is the starting day of the week and its index is 0.
        //'America/New_York; 1_08:00-1_17:00; 2_08:00-2_17:00; 3_08:00-3_17:00; 4_08:00-4_21:33; 5_08:00-5_17:00; 6_13:00-6_17:00;'
        let str = `${state.selectedTimeZone}; `;

        state.time_ranges.forEach((range) => {
            str += `${range.day}_${range.from}-${range.day}_${range.to}; `;
        });

        match.value.value = str.trim();
    },
    {
        deep: true,
    }
);

watch(
    () => match.value.operator,
    () => {
        if (nullableValue.value) {
            match.value.value = null;
        }
    }
);

watch(
    () => match.value.data_type,
    async () => {
        await nextTick();
        if (!operators.value.find((operator) => operator.value == match.value.operator)) {
            match.value.operator = null;
        }
    }
);

watch(
    () => match.value.attribute_name,
    async (attributeName, previousAttributeName) => {
        if (!customAttributeKeyAndValue()) {
            match.value.data_type = null;
            match.value.key = null;
        }

        // When the previous attribute have options we only retain the value
        // in case it exists in the new available valueOptions
        const valueExists = Boolean(valueOptions().find((option) => option.value === match.value.value));
        if (valueOptions(previousAttributeName).length && !valueExists) {
            match.value.value = null;
        }
        // When previous was current time unsets the value
        if (currentTimeAttribute(previousAttributeName)) {
            match.value.value = null;
        }

        if (apiCall.value) {
            match.value.value = null;
        }

        // If operator does not exist clear it. In case there are options set to equals.
        // When there are valueOptions some cases do not require an operator or have
        // a predefined operator such as subscription status
        const operatorExists = Boolean(operators.value.find((operator) => operator.value === match.value.operator));
        if (!operatorExists) {
            match.value.operator = valueOptions().length ? MATCH_EQUALS_OPERATOR.value : null;
        }

        if (currentTimeAttribute()) {
            match.value.operator = 'in_range';
            state.time_ranges = [
                { id: randomString(), day: 1, from: '08:00', to: '17:00' },
                { id: randomString(), day: 2, from: '08:00', to: '17:00' },
                { id: randomString(), day: 3, from: '08:00', to: '17:00' },
                { id: randomString(), day: 4, from: '08:00', to: '17:00' },
                { id: randomString(), day: 5, from: '08:00', to: '17:00' },
                { id: randomString(), day: 6, from: '08:00', to: '17:00' },
                { id: randomString(), day: 0, from: '08:00', to: '17:00' },
            ];
        }

        fetchOptions();
    }
);

const areaCodesOptions = phoneAreaCodes.map((pac) => ({
    value: pac.internal,
    label: pac.state,
}));

const valueOptions = (attributeName) => {
    if (entryPointAttribute(attributeName)) {
        return entryPointsForSelect();
    }

    if (subscriptionStatusAttribute(attributeName)) {
        return subscriptionStatusForSelect();
    }

    if (originTypeAttribute(attributeName)) {
        return [
            {
                value: 'App\\CampaignMessage',
                label: 'Is Campaign Message',
                operator_value: MATCH_EQUALS_OPERATOR.value,
            },
            {
                value: 'App\\AutomatedMessage',
                label: 'Is Automated Message',
                operator_value: MATCH_EQUALS_OPERATOR.value,
            },
            {
                value: 'App\\User,App\\ScheduledMessage',
                label: 'Is User Message',
                operator_value: MATCH_EQUALS_ANY_OPERATOR.value,
            },
        ];
    }

    if (phoneStateAttribute(attributeName)) {
        return areaCodesOptions;
    }

    if (messageDirectionAttribute(attributeName)) {
        return [
            { value: 'inbound', label: 'Is Inbound' },
            { value: 'outbound', label: 'Is Outbound' },
        ];
    }

    if (messageBodySentimentAttribute(attributeName)) {
        return [
            { value: 'positive', label: 'Is Positive' },
            { value: 'negative', label: 'Is Negative' },
            { value: 'neutral', label: 'Is Neutral' },
        ];
    }

    if (messageBodyWantsOptOutAttribute(attributeName)) {
        return [
            { value: 'true', label: 'Is True' },
            { value: 'false', label: 'Is False' },
        ];
    }
    return [];
};

const operators = computed(() => {
    if (!match.value.attribute_name) {
        return [];
    }

    if (match.value.attribute_name == 'current_time') {
        return MATCH_OPERATORS.current_time;
    }

    if (originTypeAttribute()) {
        return [MATCH_EQUALS_OPERATOR, MATCH_EQUALS_ANY_OPERATOR];
    }

    if (messageDirectionAttribute() || messageBodySentimentAttribute() || messageBodyWantsOptOutAttribute()) {
        return [MATCH_EQUALS_OPERATOR];
    }

    if (mediaUrlAttribute()) {
        return [
            { value: MATCH_IS_NULL_OPERATOR.value, label: 'Is Not Present' },
            { value: MATCH_IS_NOT_NULL_OPERATOR.value, label: 'Is Present' },
        ];
    }

    if (match.value.attribute_name === 'contact.tags.tag_name') {
        return MATCH_OPERATORS.includes;
    }

    if (
        match.value.attribute_name === 'message.antecedent_automation_rule_id' ||
        match.value.attribute_name === 'message.antecedent_campaign_id'
    ) {
        return [MATCH_EQUALS_OPERATOR];
    }

    if (
        match.value.attribute_name === 'short_url_click.message_automation_rule_id' ||
        match.value.attribute_name === 'short_url_click.message_campaign_id'
    ) {
        return [MATCH_EQUALS_OPERATOR];
    }

    if (subscriptionStatusAttribute()) {
        return [MATCH_EQUALS_OPERATOR];
    }

    if (entryPointAttribute()) {
        return MATCH_OPERATORS.contact_entrypoint;
    }

    if (phoneStateAttribute()) {
        return MATCH_OPERATORS.contact_phone_state;
    }

    if (match.value.attribute_name === 'campaign.id') {
        return MATCH_OPERATORS.equals;
    }

    if (campaignIdAttribute()) {
        return MATCH_OPERATORS.includes;
    }

    /**
     * Moving on to checks for variable type
     */
    if (match.value.data_type) {
        if (match.value.data_type === 'number') {
            return MATCH_OPERATORS.numeric_data_type;
        } else {
            return MATCH_OPERATORS.text_data_type;
        }
    } else if (isAttrAny()) {
        return [].concat(MATCH_OPERATORS.any, MATCH_OPERATORS.boolean, MATCH_OPERATORS.string, MATCH_OPERATORS.numeric);
    } else if (isAttrString()) {
        return [].concat(MATCH_OPERATORS.any, MATCH_OPERATORS.string);
    } else if (isAttrNumeric()) {
        return [].concat(MATCH_OPERATORS.any, MATCH_OPERATORS.numeric);
    }

    return MATCH_OPERATORS.any;
});

const isAttrString = () => {
    if (isAttrAny()) {
        return true;
    }

    if (isAttrNumeric()) {
        return false;
    }

    // everything not numeric or ANY should be a string
    return true;
};

const isAttrNumeric = () => {
    if (isAttrAny()) {
        return true;
    }

    if (match.value.attribute_name.match(/_at$/)) {
        return true;
    }

    return false;
};

const isAttrAny = () =>
    match.value.attribute_name === 'custom_attribute.value' ||
    match.value.attribute_name === 'contact.custom_attributes.value';

const fetchOptions = debounce((search) => {
    if (!apiCall.value) {
        return;
    }

    state.autocompleteLoading = true;
    axios
        .get(apiCall.value.url, {
            params: {
                query: search,
            },
        })
        .then((response) => {
            state.autocompleteItems = response.data.data.map((row) => {
                if (typeof row === 'string') {
                    return {
                        label: row,
                        value: row,
                    };
                }

                const label = row.name || row.value;
                const value = String(row.id || row.value);
                return {
                    label: `${value && apiCall.value.showValue ? value + ' - ' : ''}${label}`,
                    value: value,
                };
            });
        })
        .catch((e) => {
            state.autocompleteItems = [];
            console.error(e);
            toasted.global.platform_error();
        })
        .finally(() => {
            state.autocompleteLoading = false;
        });
}, 300);

const showOperator = computed(() => {
    if (!match.value.attribute_name) {
        return false;
    }

    // Custom attribute key AND value has a special logic to display the value and operator. Check the template.
    if (customAttributeKeyAndValue()) {
        return false;
    }

    return ![
        'message.origin_type',
        'message.direction',
        'message.sentiment',
        'message.wants_opt_out',
        'contact.marketing_subscription',
        'contact.transactional_subscription',
        'contact.previous_marketing_subscription',
        'contact.previous_transactional_subscription',
    ].includes(match.value.attribute_name);
});

const showValue = computed(() => {
    if (nullableValue.value) {
        return false;
    }

    // Custom attribute key AND value has a special logic to display the value and operator. Check the template.
    if (customAttributeKeyAndValue()) {
        return false;
    }

    // Current time have special logic
    if (currentTimeAttribute()) {
        return false;
    }

    if (mediaUrlAttribute()) {
        return false;
    }

    if (!match.value.attribute_name) {
        return false;
    }

    return true;
});

const removeTimeRange = (rangeIndex) => {
    if (state.time_ranges.length === 1) {
        toasted.error('At least one day range is required.');

        return;
    }

    state.time_ranges = state.time_ranges.filter((currentRange, currentRangeIndex) => currentRangeIndex != rangeIndex);
};

const addTimeRange = (afterRangeIndex) => {
    state.time_ranges.splice(afterRangeIndex + 1, 0, makeTimeRange());
};

const makeTimeRange = () => {
    return { id: randomString(), day: 1, from: '08:00', to: '17:00' };
};
</script>
